<template>
  <v-form ref="form" v-model="valid">
    <v-text-field
      outlined
      :rules="nameRule"
      :required="true"
      :label="$t('report.witness.firstName') + ' *'"
      v-model="witness.firstName"
      data-test-witness-first-name
    />
    <v-text-field
      outlined
      :rules="nameRule"
      :required="true"
      :label="$t('report.witness.lastName') + ' *'"
      v-model="witness.lastName"
      data-test-witness-last-name
    />
    <v-text-field
      outlined
      :rules="phoneNumberRules"
      :required="required"
      :label="$t('report.witness.phone') + (required ? ' *' : '')"
      v-model="witness.contact.phone"
      data-test-witness-phone
    />
    <v-text-field
      outlined
      :rules="emailRules"
      :required="required"
      :label="$t('report.witness.email') + (required ? ' *' : '')"
      v-model="witness.contact.email"
      data-test-witness-mail
    />
    <v-autocomplete
      v-if="!role"
      :rules="roleRules"
      outlined
      :required="required"
      :label="$t('report.witness.role') + (required ? ' *' : '')"
      v-model="witness.role"
      :items="witnessRolesItems"
      data-test-witness-role
    >
      <template #item="{ item }"> {{ $t(`report.witness.roles.${item}`) }} </template>
      <template #selection="{ item }"> {{ $t(`report.witness.roles.${item}`) }} </template>
    </v-autocomplete>

    <v-checkbox v-if="showInsurance" v-model="hasInsurance" :label="$t(`report.witness.insurance`)"></v-checkbox>
    <insurance-form v-if="hasInsurance" v-model="witness.insurances[0]" :types="insuranceTypes" :type="type" />

    <v-text-field
      v-if="numberplate"
      outlined
      :label="$t('report.witness.numberplate')"
      :rules="numberplateRules"
      v-model="witness.numberplate"
      data-test-witness-numberplate
    />

    <v-checkbox v-if="showInjury" v-model="witness.isInjured" :label="$t(`report.witness.isInjured`)"></v-checkbox>
    <v-textarea
      outlined
      :required="required"
      :label="$t(`report.witness.isInjuredDetail`) + (required ? ' *' : '')"
      v-model="witness.note"
      :rules="noteRule"
      v-if="witness.isInjured"
    ></v-textarea>
    <v-card-text v-if="showLicense">
      {{ $t(`report.witness.appendix`) }}
    </v-card-text>
    <report-image-upload
      v-if="showLicense"
      :asset="assetFront"
      :example="exampleFront"
      :instruction="instruction"
      :type="typeFront"
      :imageIds="witness.imageIds"
      @onImageUpload="(image) => onImageUpload(image)"
      @onImageDeleted="(image) => onImageDeleted(image)"
      data-test-image
    />
  </v-form>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { emailRuleOptional, phoneRule } from "@/lib/rules/contactRule";
import { numberplateRule } from "@/lib/rules/numberplateRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { IImageUploaded } from "@/models/Image/IImageUploaded";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { MrfiktivCreateReportWitnessDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import ReportImageUpload from "@/views/report/ReportImageUpload.vue";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import NumberplateForm from "../utility/NumberplateForm.vue";
import InsuranceForm, { InsuranceTypeEnum } from "./InsuranceForm.vue";

@Component({
  components: {
    LayoutReportStep,
    ReportImageUpload,
    NumberplateForm,
    InsuranceForm
  }
})
export default class WitnessForm extends Vue {
  @Prop({ default: true })
  required!: boolean;

  @Prop({ default: false })
  numberplate!: boolean;

  @Prop()
  role?: ReportRoleEnum;

  @Prop({ default: false })
  showInsurance!: boolean;

  @Prop({ default: true })
  showInjury!: boolean;

  @Prop({ default: true })
  showLicense!: boolean;

  @Prop()
  value!: MrfiktivCreateReportWitnessDtoGen;

  assetFront = AssetEnum.driversLicenseExampleFrontOutlined;
  exampleFront = AssetEnum.driversLicenseExampleFront;

  instruction: IReportInstructionData = {
    title: "",
    text: "",
    pictures: [AssetEnum.registration],
    example: AssetEnum.registration,
    display: false
  };

  witness: MrfiktivCreateReportWitnessDtoGen = this.value ?? {
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    },
    imageIds: [],
    role: this.role as ReportRoleEnum,
    isInjured: false,
    note: "",
    numberplate: "",
    insurances: []
  };

  ReportRoleEnum = ReportRoleEnum;
  InsuranceTypeEnum = InsuranceTypeEnum;

  valid = false;
  hasInsurance = false;

  @Watch("valid", { deep: true })
  emitValid() {
    this.$emit("valid", this.valid);
  }

  @Watch("witness", { deep: true })
  emitUpdated() {
    this.$emit("input", this.witness);
  }

  @Watch("hasInsurance")
  toggleInsurance() {
    if (!this.hasInsurance) {
      this.witness.insurances = [];
    } else {
      this.witness.insurances = [
        { name: "", number: "", type: InsuranceTypeEnum.MOTORISTS_THIRD_PARTY_LIABILITY_COVERAGE }
      ];
    }
  }

  get isDone() {
    return this.valid;
  }

  get insuranceTypes() {
    return [InsuranceTypeEnum.MOTORISTS_THIRD_PARTY_LIABILITY_COVERAGE];
  }

  get type() {
    return InsuranceTypeEnum.MOTORISTS_THIRD_PARTY_LIABILITY_COVERAGE;
  }

  get witnessRolesItems() {
    return Object.values(ReportRoleEnum);
  }

  get typeFront() {
    return ReportImageType.driverslicensefront;
  }

  get numberplateRules() {
    const rules = [];

    rules.push(numberplateRule());

    return rules;
  }

  get emailRules() {
    const rules = [];
    if (this.required) {
      rules.push(requiredRule());
    }
    rules.push(emailRuleOptional());

    return rules;
  }

  get nameRule() {
    const rules = [];
    rules.push(requiredRule());

    return rules;
  }

  get noteRule() {
    const rules = [];
    if (this.required) {
      rules.push(requiredRule());
    }

    return rules;
  }

  get phoneNumberRules() {
    const rules = [];

    if (this.required) {
      rules.push(requiredRule());
    }
    rules.push(phoneRule());

    return rules;
  }

  get roleRules() {
    const rules = [];

    if (this.required) {
      rules.push(requiredRule());
    }
    return rules;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  onImageUpload(image: IImageUploaded) {
    this.witness.imageIds.push(image.uploadId);
  }

  onImageDeleted(images: IImageUploaded[]) {
    const removed = images.map((i) => i.uploadId);
    const filtered = this.witness.imageIds.filter((i: string) => {
      return !removed.includes(i);
    });

    this.witness.imageIds.splice(0);
    this.witness.imageIds.push(...filtered);
  }
}
</script>
